// src/component/datatable2/DataTable.tsx
//
// DataTable2: a PrimeReact DataTable with server-side paging, sorting,
// inline header filters, the filter panel, active filter chips and
// cross-page row selection.
//
// This component only wires state to markup. The logic lives in
// framework-free, unit-tested classes:
//   models/ColumnLayoutModel     column widths (actions column, auto widths)
//   models/FilterCriteriaModel   immutable changes of the search criteria
//   models/ActiveFilterChips     chips above the table
//   models/FilterDisplayValue    texts of filter values
//   models/TextFilterOptions     options of the text filter panel
//   models/SortCycle             none → asc → desc → none
//   models/SelectionModel        selection across pages
//   models/PageInput             "go to page" input
//   models/DataTableCriteriaModel criteria sent to fetchData
//   viewmodels/DataTableViewModel filter objects and filter panel params

import React, {
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { DataTable as PrimeReactDataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { classNames } from 'primereact/utils';

import FilterPanel from '../filterpanel2/FilterPanel';

import { DataTableContext, DataTableProvider } from './DataTableContext';
import type { ColumnProps, DataTableHandle, DataTableProps } from './types';
import type { FilterType } from './types/FilterType';
import { FILTER_TYPES } from './constants/FilterTypes';
import { DataTableCriteriaModel } from './models/DataTableCriteriaModel';
import { ColumnLayoutModel, type ColumnLayoutItem } from './models/ColumnLayoutModel';
import { FilterCriteriaModel } from './models/FilterCriteriaModel';
import { ActiveFilterChips, type ActiveFilterChip } from './models/ActiveFilterChips';
import { FilterDisplayValue } from './models/FilterDisplayValue';
import { TextFilterOptions } from './models/TextFilterOptions';
import { SortCycle } from './models/SortCycle';
import { SelectionModel } from './models/SelectionModel';
import { PageInput } from './models/PageInput';
import { DataTableViewModel } from './viewmodels/DataTableViewModel';
import { useContainerWidth } from './hooks/useContainerWidth';
import { useHorizontalOverflow } from './hooks/useHorizontalOverflow';
import { useFieldDebounce } from './hooks/useFieldDebounce';
import { InlineFilterHeader } from './components/InlineFilterHeader';
import { PlainColumnHeader } from './components/PlainColumnHeader';
import { ActiveFiltersBar } from './components/ActiveFiltersBar';
import { DataTableToolbar } from './components/DataTableToolbar';
import { ROWS_PER_PAGE_OPTIONS, createPaginatorTemplate } from './components/PaginatorTemplate';

import { RenderedHelper } from '../../utils/render/RenderedHelper';
import { Constants } from '../../utils/Constants';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import './DataTable.scss';

const DEFAULT_ROWS = 25;

/** Delay before typing in an inline filter or the global search is applied. */
const FILTER_DELAY = 600;

const LAST_DATA_COLUMN_CLASS = 'dt2-last-data-column';

type DataTableExpansionProps = DataTableProps & {
    dataKey?: string;
    expandedRows?: any;
    onRowToggle?: (event: any) => void;
    onRowExpand?: (event: any) => void;
    onRowCollapse?: (event: any) => void;
    rowExpansionTemplate?: (rowData: any) => React.ReactNode;
};

type ColumnElement = React.ReactElement<ColumnProps>;

const layoutItemOf = (child: ColumnElement): ColumnLayoutItem => ({
    key: child.key as string,
    width: child.props.style?.width,
    minWidth: child.props.style?.minWidth,
    classNames: [child.props.headerClassName, child.props.bodyClassName, child.props.className],
});

const appendClass = (value: string | undefined, className: string) => `${value ?? ''} ${className}`;

const DataTableInner = forwardRef<DataTableHandle, DataTableProps>((props, ref) => {
    const {
        filterConfig,
        defaultSearchCriteria,
        fetchData,
        tableToolbarLeft,
        tableToolbarRight,
        tableToolbarBottom,
        selectable = false,
        onSelectionChange,
        onRowSelect,
        onRowUnselect,
        onRowClick,
        rowClassName,
        selectedRowId,
        globalSearchPosition = 'left',
        minWidth = '800px',
        showRowsPerPage = true,
        showPaginator = true,
        dataKey = 'id',
        expandedRows,
        onRowToggle,
        onRowExpand,
        onRowCollapse,
        rowExpansionTemplate,
        children,
    } = props as DataTableExpansionProps;

    const { t } = useTranslation();
    const { searchCriteria, setSearchCriteria } = useContext(DataTableContext);

    const vm = useMemo(() => new DataTableViewModel(filterConfig), [filterConfig]);

    const filterDialog = useRef<any>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const baseQueryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const selectedAllRef = useRef(false);
    const totalRecordsRef = useRef(0);
    const defaultCriteriaInitializedRef = useRef(false);

    const containerWidth = useContainerWidth(wrapperRef);
    const fieldDebounce = useFieldDebounce(FILTER_DELAY);

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [baseQuery, setBaseQuery] = useState<string | undefined>(undefined);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageInputTooltip, setPageInputTooltip] = useState(t('dataTable.pageInputTooltip'));
    const [filterPanelState, setFilterPanelState] = useState<any>({});
    const [selected, setSelected] = useState<any[]>([]);
    const [selectionExceptions, setSelectionExceptions] = useState<string[]>([]);
    const [selectedAll, setSelectedAll] = useState(false);

    /* ---------------- columns & widths ---------------- */

    const columns = React.Children.toArray(children).filter(React.isValidElement) as ColumnElement[];

    const autoWidths = useMemo(
        () => ColumnLayoutModel.computeWidths(
            (React.Children.toArray(children).filter(React.isValidElement) as ColumnElement[]).map(layoutItemOf),
            Math.min(containerWidth, ColumnLayoutModel.DESIGN_TABLE_WIDTH),
        ),
        [children, containerWidth],
    );

    // Wide tables scroll inside their wrapper; the classes drive the
    // pinned actions column's shadow (see "Wide tables" in DataTable.scss).
    const horizontalOverflow = useHorizontalOverflow(wrapperRef, '.p-datatable-wrapper', [data, autoWidths, loading]);

    /* ---------------- imperative handle ---------------- */

    const decorateCriteria = useCallback(
        (criteria: any) => DataTableCriteriaModel.decorate(criteria, filterConfig),
        [filterConfig],
    );

    const clearSelection = useCallback(() => {
        setSelectedAll(false);
        setSelectionExceptions([]);
        setSelected([]);
    }, []);

    const refresh = useCallback((params: { clearSelections?: boolean } = {}) => {
        if (params.clearSelections) {
            setSelectedAll(false);
            setSelectionExceptions([]);
        }
        setSearchCriteria((prev: any) => ({ ...prev, timestamp: Date.now() }));
    }, [setSearchCriteria]);

    useImperativeHandle(ref, () => ({
        refresh,
        clearSelection,
        selectedAll,
        selectionExceptions,
        decorateCriteria,
        getTotalRecords: () => totalRecords,
    }));

    /* ---------------- search criteria ---------------- */

    // Default criteria fill whatever the context does not set yet (once).
    useEffect(() => {
        if (!defaultSearchCriteria || defaultCriteriaInitializedRef.current) return;

        defaultCriteriaInitializedRef.current = true;

        setSearchCriteria((prev: any) => ({
            ...defaultSearchCriteria,
            ...prev,
            first: prev?.first ?? defaultSearchCriteria.first,
            rows:
                prev?.rows === DEFAULT_ROWS
                    ? defaultSearchCriteria.rows
                    : prev?.rows ?? defaultSearchCriteria.rows,
            sortField:
                !prev?.sortField || prev.sortField === 'null'
                    ? defaultSearchCriteria.sortField
                    : prev.sortField,
            sortOrder:
                prev?.sortOrder === null || prev?.sortOrder === undefined
                    ? defaultSearchCriteria.sortOrder
                    : prev.sortOrder,
            baseQuery: prev?.baseQuery ?? defaultSearchCriteria.baseQuery,
            filters: prev?.filters ?? defaultSearchCriteria.filters,
        }));
    }, [defaultSearchCriteria, setSearchCriteria]);

    useEffect(() => { selectedAllRef.current = selectedAll; }, [selectedAll]);

    useEffect(() => {
        if (searchCriteria?.baseQuery !== undefined) setBaseQuery(searchCriteria.baseQuery);
    }, [searchCriteria?.baseQuery]);

    useEffect(() => {
        if (typeof searchCriteria?.first === 'number' && typeof searchCriteria?.rows === 'number') {
            setCurrentPage(FilterCriteriaModel.pageOf(searchCriteria.first, searchCriteria.rows));
        }
    }, [searchCriteria?.first, searchCriteria?.rows]);

    const searchCriteriaWithoutPagination = useMemo(
        () => DataTableCriteriaModel.withoutPagination(searchCriteria),
        [searchCriteria],
    );

    useEffect(() => { setSelectionExceptions([]); }, [searchCriteriaWithoutPagination]);

    // Global search: applied 600 ms after the last keystroke.
    useEffect(() => {
        if (baseQuery == null) return;
        if (baseQueryTimeoutRef.current) clearTimeout(baseQueryTimeoutRef.current);
        baseQueryTimeoutRef.current = setTimeout(() => {
            if (baseQuery !== searchCriteria?.baseQuery) {
                setSearchCriteria((prev: any) => FilterCriteriaModel.withBaseQuery(prev, baseQuery));
            }
        }, FILTER_DELAY);
    }, [baseQuery, searchCriteria?.baseQuery, setSearchCriteria]);

    useEffect(() => () => {
        if (baseQueryTimeoutRef.current) clearTimeout(baseQueryTimeoutRef.current);
    }, []);

    /* ---------------- data loading ---------------- */

    const fetchDataRef = useRef(fetchData);
    const vmRef = useRef(vm);
    const filterConfigRef = useRef(filterConfig);

    useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);
    useEffect(() => { vmRef.current = vm; }, [vm]);
    useEffect(() => { filterConfigRef.current = filterConfig; }, [filterConfig]);

    useEffect(() => {
        const decorated = DataTableCriteriaModel.decorate(searchCriteria, filterConfigRef.current);
        if (!searchCriteria || !decorated) return undefined;

        const totalPages = Math.ceil(totalRecordsRef.current / decorated.rows);
        const page = FilterCriteriaModel.pageOf(decorated.first, decorated.rows);
        if (totalPages > 0 && page > totalPages) return undefined;

        let cancelled = false;
        setLoading(true);

        fetchDataRef.current(decorated)
            .then(response => {
                if (cancelled) return;
                const nextTotalRecords = response?.totalRecords ?? 0;
                setData(vmRef.current.normalizeDataDates(response?.data ?? []));
                setTotalRecords(nextTotalRecords);
                totalRecordsRef.current = nextTotalRecords;
            })
            .catch(() => {
                if (cancelled) return;
                setData([]);
                setTotalRecords(0);
                totalRecordsRef.current = 0;
            })
            .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    }, [searchCriteria]);

    /* ---------------- selection ---------------- */

    useEffect(() => {
        setSelected(SelectionModel.selectedRows(data, selectedAll, selectionExceptions));
    }, [data, selectedAll, selectionExceptions]);

    useEffect(() => {
        setSelected(selectedAll ? data : []);
        setSelectionExceptions([]);
    }, [selectedAll, data]);

    useEffect(() => {
        onSelectionChange?.(SelectionModel.count(selectedAll, totalRecords, selectionExceptions.length));
    }, [selected, selectedAll, totalRecords, selectionExceptions.length, onSelectionChange]);

    const handleSelectionChange = useCallback((event: any) => {
        setSelectionExceptions(prev => SelectionModel.exceptionsAfterChange(prev, data, event.value, selectedAllRef.current));
        setSelected(event.value);
    }, [data]);

    /* ---------------- filters ---------------- */

    const commitFilter = useCallback((fieldName: string, type: FilterType, value: any) => {
        setSearchCriteria((prev: any) => FilterCriteriaModel.withFilter(prev, fieldName, vm.createFilter(fieldName, type, value)));
    }, [setSearchCriteria, vm]);

    const handleInlineFilterChange = useCallback((fieldName: string, type: FilterType, value: any) => {
        fieldDebounce.schedule(fieldName, () => commitFilter(fieldName, type, value));
    }, [commitFilter, fieldDebounce]);

    const handleInlineFilterClear = useCallback((fieldName: string) => {
        filterDialog.current?.hide();
        fieldDebounce.cancel(fieldName);
        setSearchCriteria((prev: any) => FilterCriteriaModel.withoutFilter(prev, fieldName));
    }, [fieldDebounce, setSearchCriteria]);

    const handleFilterClick = useCallback((
        event: React.MouseEvent | React.KeyboardEvent | React.FocusEvent,
        fieldName: string,
        type: FilterType,
        params: any,
    ) => {
        event.preventDefault?.();
        event.stopPropagation();

        if (type === FILTER_TYPES.TEXT) {
            setFilterPanelState({
                fieldName,
                filterType: type,
                filterParams: {
                    value: FilterDisplayValue.panelSearchText(searchCriteria?.filters?.[fieldName]?.value, params),
                    options: TextFilterOptions.fromRows(data, fieldName),
                },
            });
        } else {
            setFilterPanelState({
                fieldName,
                filterType: type,
                filterParams: vm.enhanceFilterParams(fieldName, type, params, searchCriteria),
            });
        }

        // Let the panel render the new filter before it opens.
        setTimeout(() => {
            filterDialog.current?.toggle(event);
        }, 0);
    }, [data, searchCriteria, vm]);

    const applyFilter = useCallback((type: FilterType, params: any) => {
        commitFilter(filterPanelState.fieldName, type, params);
    }, [commitFilter, filterPanelState.fieldName]);

    const removeChip = useCallback((chip: ActiveFilterChip) => {
        setSearchCriteria((prev: any) => FilterCriteriaModel.withoutChip(prev, chip));
    }, [setSearchCriteria]);

    const clearAllFilters = useCallback(() => {
        setSearchCriteria((prev: any) => FilterCriteriaModel.withoutFilters(prev));
    }, [setSearchCriteria]);

    const chipBuilder = useMemo(() => new ActiveFilterChips({
        t,
        formatDate: (date, withTime) => moment(date as any).format(withTime ? Constants.DATE_TIME_FORMAT : Constants.DATE_FORMAT),
        formatBoolean: value => RenderedHelper.formatBoolean(value),
    }), [t]);

    const activeFilters = useMemo(
        () => chipBuilder.build(searchCriteria?.filters),
        [chipBuilder, searchCriteria?.filters],
    );

    /* ---------------- sorting & paging ---------------- */

    const handleSortToggle = useCallback((fieldName: string) => {
        setSearchCriteria((prev: any) => SortCycle.next(prev, fieldName));
    }, [setSearchCriteria]);

    const onPageChange = useCallback((event: any) => {
        setSearchCriteria((prev: any) => FilterCriteriaModel.withPage(prev, event.first, event.rows));
        setCurrentPage(event.page + 1);
    }, [setSearchCriteria]);

    const onPageInputCommit = useCallback((event: any, options: any) => {
        if (event.key !== 'Enter') return;

        const { page, valid } = PageInput.resolve(currentPage, options.props.totalPages);
        const first = FilterCriteriaModel.firstRowOf(page, searchCriteria.rows);

        if (!valid) {
            setPageInputTooltip(t('dataTable.pageInputErrorTooltip', { totalPages: options.props.totalPages }));
            setCurrentPage(page);
        } else {
            setPageInputTooltip(t('dataTable.pageInputTooltip'));
        }

        setSearchCriteria((prev: any) => ({ ...prev, first }));
    }, [currentPage, searchCriteria?.rows, setSearchCriteria, t]);

    /* ---------------- column headers ---------------- */

    const buildColumn = useCallback((child: ColumnElement) => {
        if (ColumnLayoutModel.isActionsColumn(layoutItemOf(child))) {
            const fixedStyle = ColumnLayoutModel.actionsColumnStyle();

            return React.cloneElement<ColumnProps>(child, {
                header: <div className="dt2-actions-col-header" style={fixedStyle} />,
                sortable: false,
                headerStyle: fixedStyle,
                bodyStyle: { ...fixedStyle, textAlign: 'center' },
                style: fixedStyle,
                headerClassName: appendClass(child.props.headerClassName, ColumnLayoutModel.ACTIONS_COLUMN_CLASS),
                bodyClassName: appendClass(child.props.bodyClassName, ColumnLayoutModel.ACTIONS_COLUMN_CLASS),
                className: appendClass(child.props.className, ColumnLayoutModel.ACTIONS_COLUMN_CLASS),
            });
        }

        // Columns without an explicit width get their computed share.
        const hasExplicitWidth = ColumnLayoutModel.parsePixels(child.props.style?.width) != null;
        const computedWidth = hasExplicitWidth ? undefined : autoWidths.get(child.key as string);
        const style = computedWidth
            ? { ...child.props.style, width: computedWidth, minWidth: computedWidth }
            : child.props.style;

        const fieldName = child.props.sortField || child.props.field;
        const label = child.props.header as string;
        const config = filterConfig.find(item => item.fieldName === fieldName);

        const header = config ? (
            <InlineFilterHeader
                config={config}
                currentFilters={searchCriteria?.filters ?? {}}
                sortField={searchCriteria?.sortField}
                sortOrder={searchCriteria?.sortOrder}
                onFilterChange={handleInlineFilterChange}
                onFilterClear={handleInlineFilterClear}
                onFilterClick={handleFilterClick}
                onSortToggle={handleSortToggle}
                label={label}
            />
        ) : (
            <PlainColumnHeader
                label={label}
                fieldName={fieldName}
                sortable={!!child.props.sortable}
                sortField={searchCriteria?.sortField}
                sortOrder={searchCriteria?.sortOrder}
                onSortToggle={handleSortToggle}
            />
        );

        return React.cloneElement<ColumnProps>(child, { style, header, sortable: false });
    }, [
        autoWidths,
        filterConfig,
        searchCriteria?.filters,
        searchCriteria?.sortField,
        searchCriteria?.sortOrder,
        handleInlineFilterChange,
        handleInlineFilterClear,
        handleFilterClick,
        handleSortToggle,
    ]);

    // Without an actions column, the last data column gets the right-hand border.
    const hasActionsColumn = columns.some(child => ColumnLayoutModel.isActionsColumn(layoutItemOf(child)));
    const lastDataColumnKey = hasActionsColumn ? null : columns[columns.length - 1]?.key;

    const enhancedColumns = columns.map(child => {
        const built = buildColumn(child);

        if (child.key !== lastDataColumnKey) {
            return built;
        }

        return React.cloneElement<ColumnProps>(built, {
            headerClassName: appendClass(built.props.headerClassName, LAST_DATA_COLUMN_CLASS),
            className: appendClass(built.props.className, LAST_DATA_COLUMN_CLASS),
        });
    });

    /* ---------------- render ---------------- */

    return (
        <>
            <FilterPanel
                ref={filterDialog}
                onSortAsc={false}
                onSortDesc={false}
                type={filterPanelState.filterType}
                params={filterPanelState.filterParams}
                applyFilter={applyFilter}
                timestamp={filterPanelState.timestamp}
            />

            <div
                className={classNames('data-table data-table--inline-filters', {
                    'dt2-overflow': horizontalOverflow.overflowing,
                    'dt2-overflow--start': horizontalOverflow.hiddenStart,
                    'dt2-overflow--end': horizontalOverflow.hiddenEnd,
                })}
            >
                <DataTableToolbar
                    left={tableToolbarLeft}
                    right={tableToolbarRight}
                    bottom={tableToolbarBottom}
                    globalSearchPosition={globalSearchPosition}
                    baseQuery={baseQuery}
                    onBaseQueryChange={setBaseQuery}
                />

                {/* wrapperRef is measured to size the auto-width columns */}
                <div className="table-wrapper" ref={wrapperRef}>
                    <ActiveFiltersBar chips={activeFilters} onRemove={removeChip} onClearAll={clearAllFilters} />

                    <PrimeReactDataTable
                        dataKey={dataKey}
                        expandedRows={expandedRows}
                        onRowToggle={onRowToggle}
                        onRowExpand={onRowExpand}
                        onRowCollapse={onRowCollapse}
                        rowExpansionTemplate={rowExpansionTemplate}
                        tableStyle={{ minWidth }}
                        value={data}
                        totalRecords={totalRecords}
                        loading={loading}
                        paginator={totalRecords > 0 && showPaginator}
                        rows={searchCriteria?.rows}
                        rowsPerPageOptions={showRowsPerPage ? ROWS_PER_PAGE_OPTIONS : []}
                        first={searchCriteria?.first}
                        className={loading && data.length === 0 ? 'dt2-loading-empty' : ''}
                        paginatorClassName="paginator"
                        lazy
                        onPage={onPageChange}
                        emptyMessage={t('dataTable.emptyMessage')}
                        rowClassName={rowClassName ?? (
                            selectedRowId
                                ? (rowData: any) => (rowData.id === selectedRowId ? 'p-highlight' : '')
                                : undefined
                        )}
                        sortField={searchCriteria?.sortField}
                        sortOrder={searchCriteria?.sortOrder}
                        selection={selectable ? selected : undefined}
                        onSelectionChange={selectable ? handleSelectionChange : undefined}
                        onRowSelect={onRowSelect}
                        onRowUnselect={onRowUnselect}
                        onRowClick={onRowClick}
                        selectionMode={selectable ? 'checkbox' : undefined}
                        selectAll={selectable ? selectedAll : undefined}
                        selectionPageOnly={selectable ? true : undefined}
                        onSelectAllChange={selectable ? (event: any) => setSelectedAll(event.checked) : undefined}
                        paginatorTemplate={createPaginatorTemplate({
                            t,
                            currentPage,
                            pageInputTooltip,
                            onCurrentPageChange: setCurrentPage,
                            onPageInputCommit,
                        })}
                    >
                        {selectable && (
                            <Column
                                key="selection"
                                selectionMode="multiple"
                                headerStyle={{ width: '3rem' }}
                            />
                        )}
                        {enhancedColumns}
                    </PrimeReactDataTable>
                </div>
            </div>
        </>
    );
});

DataTableInner.displayName = 'DataTableInner';

const DataTable = forwardRef<DataTableHandle, DataTableProps>((props, ref) => (
    <DataTableProvider>
        <DataTableInner ref={ref} {...props} />
    </DataTableProvider>
));

DataTable.displayName = 'DataTable';

export default DataTable;
